import { useAtomValue } from "jotai";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useField } from "@frontend/hooks/form";
import type { TextField } from "@frontend/lib/form/types";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";

import type { PlaceLabel } from "../../i18n/labels";
import { isEditModeAtom } from "../../stores";

export type PlaceProps = {
  field: TextField;
  labels: PlaceLabel;
};

export function Place({ field, labels }: PlaceProps) {
  const { i18n, t } = useLingui();

  const editMode = useAtomValue(isEditModeAtom);

  const { value: place, setValue: setPlace, error } = useField(field);

  const placeKey = place?.toUpperCase() as keyof PlaceLabel | undefined;

  const isUnknownPlace = placeKey != null && !(placeKey in labels);

  const translatedPlace = placeKey
    ? isUnknownPlace
      ? place
      : i18n._(labels[placeKey])
    : "";

  const items = [
    ...(isUnknownPlace && placeKey ? [{ value: placeKey, label: place }] : []),
    ...Object.entries<MessageDescriptor>(labels).map(([value, label]) => ({
      value,
      label: i18n._(label) ?? value,
    })),
  ];

  return editMode ? (
    <Select
      items={items}
      value={placeKey ?? null}
      onValueChange={(value) => setPlace(value ?? undefined)}
      size="sm"
    >
      <SelectTrigger invalid={null !== error}>
        <SelectValue placeholder={t`Select a place`} />
      </SelectTrigger>

      <SelectContent>
        {isUnknownPlace && placeKey && (
          <SelectItem value={placeKey}>{place}</SelectItem>
        )}

        {Object.entries<MessageDescriptor>(labels).map(([value, label]) => (
          <SelectItem key={value} value={value}>
            {i18n._(label) ?? value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <span>{translatedPlace}</span>
  );
}
